import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Card from "../../../../components/card/card";
import ApiPlanManagement from "../../../../api/plan-management";
import ApiTaskManagement from "../../../../api/task-managements";
import ApiProfileManagement from "../../../../api/profile-management";
import TriggeredTaskItem from "./triggered-task-item";
import {
  remindersFromActivity,
  remindersToPayload,
} from "./reminder-mapping";
import TriggeredAddTaskItem from "./triggered-add-task-item";
import useAdminEvents from "../../../../hooks/useAdminEvents";

const TriggeredTasks = ({
  planId,
  tasks = [],
  refreshPlan,
  readOnly = false,
  activeGroupScheduleIds = [],
  openNotification,
}) => {
  const { t } = useTranslation();
  const [triggeredTasks, setTriggeredTasks] = useState(tasks);
  const [tasksList, setTasksList] = useState([]);
  const [baselines, setBaselines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [timeProfileFields, setTimeProfileFields] = useState([]);

  useEffect(() => {
    setTriggeredTasks(tasks);
  }, [tasks]);

  const getTasks = (params) => {
    ApiTaskManagement.getTasks(params).then(({ data }) => {
      const { taskManagements, categories } = data || {};
      setTasksList((taskManagements || []).map((task) => ({ DailyTask: task })));
      setCategories(categories || []);
    });
  };

  const getBaselines = () => {
    ApiTaskManagement.getBaselines().then(({ data }) => {
      const { baselineList } = data || {};
      setBaselines(baselineList || []);
    });
  };

  const getTimeProfileFields = () => {
    ApiProfileManagement.getProfiles({ inputType: ["message:time"] }).then(({ data }) => {
      const { subjectProfileFields } = data || {};
      setTimeProfileFields(subjectProfileFields || []);
    });
  };

  useEffect(() => {
    getTasks({ planIds: [planId], groupScheduleIds: activeGroupScheduleIds });
    getBaselines();
    getTimeProfileFields();
  }, []);

  useEffect(() => {
    getTasks({ planIds: [planId], groupScheduleIds: activeGroupScheduleIds });
  }, [planId, JSON.stringify(activeGroupScheduleIds)]);

  useAdminEvents({
    onCategoryChanged: () => {
      getTasks({ planIds: [planId], groupScheduleIds: activeGroupScheduleIds });
    },
    onTaskChanged: () => {
      getTasks({ planIds: [planId], groupScheduleIds: activeGroupScheduleIds });
    },
  });

  const availableBaselines = useMemo(() => {
    const map = new Map();

    baselines.forEach((baseline) => {
      if (baseline?.id) {
        map.set(baseline.id, baseline);
      }
    });

    triggeredTasks.forEach((task) => {
      const field = task?.SubjectProfileField;

      if (field?.id && !map.has(field.id)) {
        map.set(field.id, {
          id: field.id,
          title: field.title,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      (a.title || "").localeCompare(b.title || "")
    );
  }, [baselines, triggeredTasks]);

  const availableTimeProfileFields = useMemo(() => {
    const map = new Map();

    timeProfileFields.forEach((field) => {
      if (field?.id) {
        map.set(field.id, field);
      }
    });

    triggeredTasks.forEach((task) => {
      const field = task?.triggerSubjectProfileField;

      if (field?.id && !map.has(field.id)) {
        map.set(field.id, {
          id: field.id,
          title: field.title,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      (a.title || "").localeCompare(b.title || "")
    );
  }, [timeProfileFields, triggeredTasks]);

  const updateLocalTriggeredTask = (activityId, patch) => {
    setTriggeredTasks((prev) =>
      prev.map((task) => (task.id === activityId ? { ...task, ...patch } : task))
    );
  };

  const addTask = (task) => {
    if (readOnly) return;

    ApiPlanManagement.upsertDateSchedule(planId, {
      dailyTaskId: task.DailyTask.id,
      // LF360-1940: see the same note in calendar.jsx — a task on its way into
      // a plan follows the task's own reminders.
      remindersCustomised: false,
    }).then(() => {
      refreshPlan();
    });
  };

  const deleteTask = (activityId) => {
    if (readOnly) return;

    ApiPlanManagement.deleteTriggeredActivity(planId, activityId).then(() => {
      refreshPlan();
    });
  };

  const updateTaskNotificationTime = (time, activityId) => {
    if (readOnly || !time) return;

    const remindHour = time.$H;
    const remindMinute = time.$m;
    const changedTask = triggeredTasks.find((t) => t.id === activityId);
    if (!changedTask) return;

    updateLocalTriggeredTask(activityId, {
      notificationType: "absolute",
      remindHour,
      remindMinute,
      remind: true,
    });

    ApiPlanManagement.upsertDateSchedule(planId, {
      ...changedTask,
      notificationType: "absolute",
      remindHour,
      remindMinute,
      remind: true,
      triggerSubjectProfileFieldId: null,
      triggerOffsetMinutes: 0,
    }).then(() => refreshPlan());
  };

  // LF360-1830: additional reminders for a triggered task. Saved through the same
  // upsert as the rest of the activity, so the backend validates them (and rejects
  // a pre-assignment reminder on a kind that cannot have one).
  // LF360-1830: a triggered task's reminders, as one list. The earliest one lands
  // in the activity's own columns and the rest in extraReminders — see
  // reminder-mapping. A task has a day, not a time of its own.
  const updateTaskReminders = (list, activityId) => {
    if (readOnly) return;

    const changedTask = triggeredTasks.find((t) => t.id === activityId);
    if (!changedTask) return;

    // LF360-1940: editing the reminders here is the plan deciding for itself, so
    // this activity stops following the task and the next publish leaves it
    // alone. Said on every reminder save rather than only the first, because the
    // marker is what the Reset affordance is drawn from.
    const patch = {
      ...remindersToPayload(list, changedTask.notificationType || "absolute"),
      remindersCustomised: true,
    };
    updateLocalTriggeredTask(activityId, patch);

    // An offset with no field yet stays local. The backend refuses it —
    // normalizeExtraReminders throws EXTRA_REMINDER_TRIGGER_FIELD_REQUIRED,
    // because a reminder with no base would be dropped by the fan-out without
    // the admin ever knowing — so there is nothing to gain by sending it.
    //
    // Asked of the list being saved, not of the activity as it was. Reading the
    // old state answered one edit too late: it let the incomplete row through to
    // be rejected, and then blocked the very edit that filled the field in.
    // A row gets no field of its own only when the editor cannot guess one, which
    // takes a project with more than one time question.
    const awaitingTriggerField = list.some(
      (reminder) =>
        reminder &&
        reminder.notificationType === "relative" &&
        !reminder.triggerSubjectProfileFieldId
    );

    if (awaitingTriggerField) {
      return;
    }

    // Deliberately no refreshPlan(): the result is already on screen, and a refresh
    // in flight overwrites local state that cannot be saved yet — a task switched to
    // Relative before a trigger field is picked (LF360-1830).
    ApiPlanManagement.upsertDateSchedule(planId, {
      ...changedTask,
      ...patch,
    });
  };

  // LF360-1940 — back to whatever the task says now.
  //
  // The same save as any other, with the marker set to false: the backend reads
  // that as "follow the task" and answers with the task's own set, so there is
  // no second route that could disagree with the first. Every task starts with
  // none, so today this clears the activity — which is the honest answer until
  // an admin gives the task some.
  const resetTaskReminders = (activityId) => {
    if (readOnly) return;

    const changedTask = triggeredTasks.find((t) => t.id === activityId);
    if (!changedTask) return;

    ApiPlanManagement.upsertDateSchedule(planId, {
      ...changedTask,
      remindersCustomised: false,
    }).then(() => refreshPlan());
  };

  const updateTaskBaseline = (baselineId, activityId) => {
    if (readOnly) return;

    const changedTask = triggeredTasks.find((t) => t.id === activityId);
    if (!changedTask) return;

    updateLocalTriggeredTask(activityId, {
      subjectProfileFieldId: baselineId,
    });

    ApiPlanManagement.upsertDateSchedule(planId, {
      ...changedTask,
      subjectProfileFieldId: baselineId,
    }).then(() => refreshPlan());
  };

  const updateTaskDuration = (duration, activityId) => {
    if (readOnly) return;

    const changedTask = triggeredTasks.find((t) => t.id === activityId);
    if (!changedTask) return;

    updateLocalTriggeredTask(activityId, { duration });

    ApiPlanManagement.upsertDateSchedule(planId, {
      ...changedTask,
      duration,
    }).then(() => refreshPlan());
  };

  const updateTaskOffsets = (offsets, activityId) => {
    if (readOnly) return;

    const changedTask = triggeredTasks.find((t) => t.id === activityId);
    if (!changedTask) return;

    const nextPatch = {
      offset: offsets,
      periodicRecurrence: offsets.length ? null : changedTask.periodicRecurrence,
    };

    updateLocalTriggeredTask(activityId, nextPatch);

    ApiPlanManagement.upsertDateSchedule(planId, {
      ...changedTask,
      offset: offsets,
      periodicRecurrence: nextPatch.periodicRecurrence,
    }).then(() => refreshPlan());
  };

  const updateTaskPeriodicRecurrence = (periodicRecurrence, activityId) => {
    if (readOnly) return;

    const changedTask = triggeredTasks.find((t) => t.id === activityId);
    if (!changedTask) return;

    const nextPatch = {
      periodicRecurrence,
      offset: periodicRecurrence ? [] : changedTask.offset || [],
    };

    updateLocalTriggeredTask(activityId, nextPatch);

    ApiPlanManagement.upsertDateSchedule(planId, {
      ...changedTask,
      offset: nextPatch.offset,
      periodicRecurrence,
    }).then(() => refreshPlan());
  };




  const allowedTaskIds = useMemo(
    () => (tasksList.length ? new Set((tasksList || []).map((t) => t.DailyTask?.id)) : null),
    [tasksList]
  );

  return (
    <Card>
      <div
        className="triggered-tasks-header"
        style={{
          display: "flex",
          alignItems: "center",
          padding: "11px 20px",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div className="plan-page_block-title" style={{ margin: 0 }}>
          {t("plan_management.triggered_tasks")}
        </div>
      </div>

      <div
        className="triggered-tasks-list"
        style={{
          padding: "10px 20px",
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        {triggeredTasks.map((task) => {
          const taskId = task?.DailyTask?.id ?? task?.id;
          const isAllowed = allowedTaskIds ? allowedTaskIds.has(taskId) : true;

          return (
            <TriggeredTaskItem
              key={task.id}
              task={task.DailyTask ?? task}
              planned={{
                remindHour: task.remindHour,
                remindMinute: task.remindMinute,
              }}
              activityId={task.id}
              deleteTask={deleteTask}
              updateTaskNotificationTime={updateTaskNotificationTime}
              reminders={remindersFromActivity(task)}
              remindersCustomised={task.remindersCustomised !== false}
              updateTaskReminders={updateTaskReminders}
              resetTaskReminders={resetTaskReminders}
              updateTaskDuration={updateTaskDuration}
              updateTaskBaseline={updateTaskBaseline}
              style={{ height: 40 }}
              baselines={availableBaselines}
              duration={task.duration}
              baselineId={task.subjectProfileFieldId}
              updateTaskOffsets={updateTaskOffsets}
              offsets={task.offset || []}
              categories={categories}
              readOnly={readOnly}
              notAllowed={!isAllowed}
              notificationType={task.notificationType || "absolute"}
              triggerSubjectProfileFieldId={task.triggerSubjectProfileFieldId || null}
              triggerOffsetMinutes={task.triggerOffsetMinutes || 0}
              timeProfileFields={availableTimeProfileFields}
              periodicRecurrence={task.periodicRecurrence || null}
              updateTaskPeriodicRecurrence={updateTaskPeriodicRecurrence}
            />
          );
        })}

        {!readOnly && (
          <TriggeredAddTaskItem
            items={tasksList}
            handleAddTask={addTask}
          />
        )}
      </div>
    </Card>
  );
};

export default TriggeredTasks;
